import type { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { formatReadableDateShortNoWeekday, safeParseISO } from 'shared/util/datetime-fns';
import type { PaleState } from '@/interfaces';
import displayValues from '@/resources/displayValues.json';

const originAirports: Record<string, string> = displayValues.originAirports;

export const SearchDetails = () => {
    const {
        origin,
        departureDate,
        returnDate,
        travelMonths,
        duration,
        ignoredFilters,
        numberOfAdults,
        agesOfChildren,
        destinationLabel,
        boundingBox,
    } = useSelector((state: PaleState) => state.filters);
    const originDisplay = originAirports[origin] ?? 'Alle flyplasser i Norge';
    const parsedDepartureDate = safeParseISO(departureDate?.date);
    const parsedReturnDate = safeParseISO(returnDate?.date);
    const destinationDisplay = destinationLabel ?? (boundingBox?.length ? 'Område i kart' : 'Hvor som helst');

    const dateInfo = (): ReactElement => {
        if (parsedDepartureDate && parsedReturnDate && !ignoredFilters?.includes('departureDate')) {
            return (
                <div data-testid="searchDetails-dateInfo-fixed">
                    <span className="whitespace-nowrap">{formatReadableDateShortNoWeekday(parsedDepartureDate)}</span>
                    <span> – </span>
                    <span className="whitespace-nowrap">{formatReadableDateShortNoWeekday(parsedReturnDate)}</span>
                </div>
            );
        }
        if (travelMonths?.length !== 0 || (duration && !ignoredFilters?.includes('travelMonths'))) {
            return (
                <div data-testid="searchDetails-dateInfo-flex">
                    {duration ? `${duration[0]} - ${duration[1]} dager` : 'Når som helst'}
                    {travelMonths?.length !== 0 && travelMonths?.length !== 12 ? ' i utvalgte måneder' : ''}
                </div>
            );
        }
        return <span data-testid="searchDetails-dateInfo-default">Når som helst</span>;
    };

    const adults = numberOfAdults ?? 2;
    const children = agesOfChildren?.length ?? 0;
    const total = adults + children;
    const mobilePax = `${total} ${total === 1 ? 'person' : 'personer'}`;
    const desktopPax = `${adults} ${adults === 1 ? 'voksen' : 'voksne'}${children > 0 ? `, ${children} barn` : ''}`;

    return (
        <div className="flex flex-row items-center gap-8 sm:gap-16 text-s md:text-m" data-testid="searchDetails">
            <div className="flex items-center gap-8 hidden md:flex">
                <w-icon name="PlaneTakeOff" size="small" locale="nb" aria-hidden></w-icon>
                <span data-testid="searchDetails-origin">{originDisplay}</span>
            </div>
            <div className="flex items-center gap-8 md:pl-16 md:border-l-1">
                <w-icon name="Map" size="small" locale="nb" className="hidden md:block" aria-hidden></w-icon>
                <span data-testid="searchDetails-destination">{destinationDisplay}</span>
            </div>
            <div className="flex items-center gap-8 pl-8 sm:pl-16 border-l-1">
                <w-icon name="Calendar" size="small" locale="nb" className="hidden md:block" aria-hidden></w-icon>
                {dateInfo()}
            </div>
            <div className="flex items-center gap-8 pl-8 sm:pl-16 border-l-1">
                <w-icon name="Users" size="small" locale="nb" className="hidden md:block" aria-hidden></w-icon>
                <span className="md:hidden" data-testid="searchDetails-pax-mobile">
                    {mobilePax}
                </span>
                <span className="hidden md:inline" data-testid="searchDetails-pax-desktop">
                    {desktopPax}
                </span>
            </div>
        </div>
    );
};
